import React from "react";
import { useTranslation } from "react-i18next";
import styles from "@/styles/components/LeadsTable.module.css";
import { useData } from "@/contexts/DataContext";
import { useAuth } from "@/contexts/AuthContext";
import { api } from "@/api";

// Simple leads table based on backend /leads endpoint structure
// lead: { actionId, actionTitle, endUserId, email, name, status, firstScanAt }

const LeadsTable = () => {
  const { t } = useTranslation();
  const { token } = useAuth();
  const { leads, refreshLeads } = useData();
  const [page, setPage] = React.useState(1);
  const [selectedActionId, setSelectedActionId] = React.useState("all");
  const [sortOrder, setSortOrder] = React.useState("newest"); // "newest" or "oldest"
  const pageSize = 20;
  
  // Get unique actions for filter
  const uniqueActions = React.useMemo(() => {
    if (!Array.isArray(leads)) return [];
    const actionsMap = new Map();
    leads.forEach(lead => {
      if (!actionsMap.has(lead.actionId)) {
        actionsMap.set(lead.actionId, {
          id: lead.actionId,
          title: lead.actionTitle
        });
      }
    });
    return Array.from(actionsMap.values()).sort((a, b) => 
      a.title.localeCompare(b.title)
    );
  }, [leads]);
  
  // Filter and sort leads
  const filteredAndSortedLeads = React.useMemo(() => {
    if (!Array.isArray(leads)) return [];
    
    // Filter by action
    let filtered = leads;
    if (selectedActionId !== "all") {
      filtered = leads.filter(lead => lead.actionId === selectedActionId);
    }
    
    // Sort by date
    const sorted = [...filtered].sort((a, b) => {
      const dateA = new Date(a.firstScanAt).getTime();
      const dateB = new Date(b.firstScanAt).getTime();
      return sortOrder === "newest" ? dateB - dateA : dateA - dateB;
    });
    
    return sorted;
  }, [leads, selectedActionId, sortOrder]);
  
  const total = filteredAndSortedLeads.length;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const currentPage = Math.min(page, totalPages);
  const start = (currentPage - 1) * pageSize;
  const end = start + pageSize;
  const pageItems = filteredAndSortedLeads.slice(start, end);
  
  React.useEffect(() => {
    setPage(1);
  }, [total]);
  
  // Reset page when filters change
  React.useEffect(() => {
    setPage(1);
  }, [selectedActionId, sortOrder]);
  const suppress = async (l) => {
    if (!token) return;
    const ok = confirm(t('merchant.leads.suppressConfirm'));
    if (!ok) return;
    try {
      await api.suppressLead(token, { endUserId: l.endUserId, actionId: l.actionId, allActions: false, reason: "manual" });
      await refreshLeads();
    } catch (e) {
      alert(e?.message || t('merchant.leads.suppressError'));
    }
  };
  const maskEmail = (email) => {
    if (!email) return "-";
    const [user, domain] = String(email).split("@");
    if (!domain) return "-";
    const [name, ...rest] = domain.split(".");
    const tld = rest.pop() || "";
    const domRest = rest.join('.') || '';
    const mask = (s) => s ? s[0] + (s.length > 1 ? "***" : "*") : "*";
    const userMasked = mask(user);
    const nameMasked = mask(name);
    const domainMasked = domRest ? `.${domRest[0]}***` : "";
    return `${userMasked}@${nameMasked}${domainMasked}${tld ? '.'+tld : ''}`;
  };
  const maskName = (name) => {
    if (!name) return "-";
    const parts = String(name).trim().split(/\s+/);
    const masked = parts.map((part) => {
      if (part.length === 0) return "";
      if (part.length === 1) return part[0] + "*";
      return part[0] + "***";
    });
    return masked.join(" ");
  };

  return (
    <div className={styles.root}>
      <div className={styles.inner}>
        {/* Filters */}
        <div className="mb-4 flex flex-col sm:flex-row gap-3">
          <div className="flex-1">
            <label className="block text-xs text-muted-foreground mb-1">
              {t('merchant.leads.filters.filterByAction')}
            </label>
            <select
              value={selectedActionId}
              onChange={(e) => setSelectedActionId(e.target.value)}
              className="w-full px-3 py-2 text-sm border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">{t('merchant.leads.filters.allActions')}</option>
              {uniqueActions.map(action => (
                <option key={action.id} value={action.id}>
                  {action.title}
                </option>
              ))}
            </select>
          </div>
          
          <div className="w-full sm:w-48">
            <label className="block text-xs text-muted-foreground mb-1">
              {t('merchant.leads.filters.sortBy')}
            </label>
            <select
              value={sortOrder}
              onChange={(e) => setSortOrder(e.target.value)}
              className="w-full px-3 py-2 text-sm border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="newest">{t('merchant.leads.filters.newestFirst')}</option>
              <option value="oldest">{t('merchant.leads.filters.oldestFirst')}</option>
            </select>
          </div>
        </div>
        
        {/* Mobile cards */}
        <div className="sm:hidden space-y-3">
          {pageItems.map((l) => (
            <div key={l.actionId + l.endUserId} className="border rounded p-3 bg-background">
              <div className="flex justify-between items-start gap-3">
                <div className="min-w-0">
                  <div className="text-xs text-muted-foreground">{t('merchant.leads.date')}</div>
                  <div className="text-sm">{new Date(l.firstScanAt).toLocaleDateString()}</div>
                </div>
                <span className={`px-2 py-1 rounded-full text-[10px] ${l.status === 'REDEEMED' ? 'bg-green-100 text-green-800' : 'bg-blue-100 text-blue-800'}`}>{l.status === 'REDEEMED' ? t('merchant.leads.redeemed') : t('merchant.leads.new')}</span>
              </div>
              <div className="mt-2 text-[11px] text-muted-foreground break-words"><span className="text-muted-foreground">{t('merchant.leads.action')}: </span>{l.actionTitle}</div>
              <div className="mt-1 text-[11px] text-muted-foreground break-words"><span className="text-muted-foreground">{t('merchant.leads.name')}: </span>{maskName(l.name)}</div>
              <div className="mt-1 text-[11px] text-muted-foreground break-all"><span className="text-muted-foreground">{t('merchant.leads.email')}: </span>{maskEmail(l.email)}</div>
            </div>
          ))}
          {total === 0 && (
            <div className="text-center text-muted-foreground text-xs py-6">{t('merchant.leads.none')}</div>
          )}
          {total > 0 && (
            <Pagination
              page={currentPage}
              totalPages={totalPages}
              setPage={setPage}
              total={total}
              pageSize={pageSize}
              className="mt-3"
            />
          )}
        </div>

        {/* Desktop table */}
        <div className={`${styles.tableWrap} hidden sm:block overflow-x-auto`}>
      <table className="w-full text-xs table-fixed">
        <thead>
          <tr className="border-b text-left text-[11px] text-muted-foreground bg-muted">
            <th className="py-2 px-2 w-[10%]">{t('merchant.leads.date')}</th>
            <th className="py-2 px-2 w-[24%]">{t('merchant.leads.action')}</th>
            <th className="py-2 px-2 w-[18%]">{t('merchant.leads.name')}</th>
            <th className="py-2 px-2 w-[24%]">{t('merchant.leads.email')}</th>
            <th className="py-2 px-2 w-[12%]">{t('merchant.leads.status')}</th>
            <th className="py-2 px-2 w-[12%] text-right">{t('merchant.leads.actions')}</th>
          </tr>
        </thead>
        <tbody>
          {pageItems.map((l) => (
            <tr
              key={l.actionId + l.endUserId}
              className="border-b odd:bg-background even:bg-muted hover:bg-muted"
            >
              <td className="py-2 px-2 align-top whitespace-nowrap text-[11px] text-foreground">
                {new Date(l.firstScanAt).toLocaleDateString()}
              </td>
              <td className="py-2 px-2 align-top text-[11px] text-foreground max-w-[260px] truncate" title={l.actionTitle}>{l.actionTitle}</td>
              <td className="py-2 px-2 align-top text-[11px] text-foreground max-w-[200px] truncate" title={maskName(l.name)}>{maskName(l.name)}</td>
              <td className="py-2 px-2 align-top text-[11px] text-foreground max-w-[260px] truncate" title={maskEmail(l.email)}>{maskEmail(l.email)}</td>
              <td className="py-2 px-2 align-top">
                <span
                  className={`px-2 py-1 rounded-full text-xs ${
                    l.status === "REDEEMED"
                      ? "bg-green-100 text-green-800"
                      : "bg-blue-100 text-blue-800"
                  }`}
                >
                  {l.status === "REDEEMED" ? t('merchant.leads.redeemed') : t('merchant.leads.new')}
                </span>
              </td>
              <td className="py-2 px-2 text-right align-top">
                <button className="text-[11px] border px-2 py-1 rounded hover:bg-muted" onClick={() => suppress(l)}>
                  {t('merchant.leads.suppress')}
                </button>
              </td>
            </tr>
          ))}
          {total === 0 && (
            <tr>
              <td colSpan={6} className="py-4 text-center text-muted-foreground text-xs">
                {t('merchant.leads.none')}
              </td>
            </tr>
          )}
        </tbody>
      </table>
      {total > 0 && (
        <div className="flex items-center justify-between mt-3 text-xs text-muted-foreground">
          <div>
            {t('merchant.leads.showing', { from: start + 1, to: Math.min(end, total), total })}
          </div>
          <Pagination
            page={currentPage}
            totalPages={totalPages}
            setPage={setPage}
            total={total}
            pageSize={pageSize}
          />
        </div>
      )}
        </div>
      </div>
    </div>
  );
};

function Pagination({ page, totalPages, setPage, className = "", total, pageSize }){
  const { t } = useTranslation();
  const maxButtons = 5;
  const half = Math.floor(maxButtons / 2);
  let start = Math.max(1, page - half);
  let end = Math.min(totalPages, start + maxButtons - 1);
  if (end - start + 1 < maxButtons) {
    start = Math.max(1, end - maxButtons + 1);
  }
  const pages = [];
  for (let p = start; p <= end; p++) pages.push(p);
  return (
    <div className={`flex items-center gap-1 ${className}`}>
      <button
        className="px-2 py-1 border rounded text-xs disabled:opacity-50"
        disabled={page <= 1}
        onClick={() => setPage(1)}
      >
        «
      </button>
      <button
        className="px-2 py-1 border rounded text-xs disabled:opacity-50"
        disabled={page <= 1}
        onClick={() => setPage(Math.max(1, page - 1))}
      >
        {t('merchant.leads.pagination.prev')}
      </button>
      {start > 1 && <span className="px-1 text-xs">…</span>}
      {pages.map((p) => (
        <button
          key={p}
          className={`px-2 py-1 border rounded text-xs ${p === page ? 'bg-muted' : 'bg-background'}`}
          onClick={() => setPage(p)}
        >
          {p}
        </button>
      ))}
      {end < totalPages && <span className="px-1 text-xs">…</span>}
      <button
        className="px-2 py-1 border rounded text-xs disabled:opacity-50"
        disabled={page >= totalPages}
        onClick={() => setPage(Math.min(totalPages, page + 1))}
      >
        {t('merchant.leads.pagination.next')}
      </button>
      <button
        className="px-2 py-1 border rounded text-xs disabled:opacity-50"
        disabled={page >= totalPages}
        onClick={() => setPage(totalPages)}
      >
        »
      </button>
    </div>
  );
}

export default LeadsTable;
